import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedPage from '../components/shared/AnimatedPage';
import { useAuthStore } from '../stores/authStore';
import {
  createProfile,
  initiateParentConsent,
  resendConsentEmail,
  finalizeUnder13Credentials,
  updateConsentEmail,
} from '../lib/functions';
import { signInAnonymously, loadProfiles } from '../hooks/useAuth';
import { auth, db } from '../lib/firebase';
import { PORTAL_URL } from '../lib/siteMode';
import type { Profile } from '../types/user';

type Step = 'dob' | 'child-info' | 'waiting' | 'password-setup' | 'rejected';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => currentYear - i);

function calculateAge(month: number, year: number): number {
  const now = new Date();
  const dob = new Date(year, month, 15);
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < 15)) {
    age--;
  }
  return age;
}

function isOAuthUser(): boolean {
  const providerData = auth.currentUser?.providerData ?? [];
  return providerData.some(
    (p) => p.providerId === 'apple.com' || p.providerId === 'google.com'
  );
}

export default function AgeGatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore((s) => s.setProfile);
  const consentPending = (location.state as { consentPending?: boolean } | null)?.consentPending;
  const stateInitialStep = (location.state as { initialStep?: Step } | null)?.initialStep;
  const stateDob = (location.state as { dob?: string } | null)?.dob;
  const stateExplorerName = (location.state as { explorerName?: string } | null)?.explorerName ?? '';
  // When navigated from AuthPage's inline under-13 form after the CF calls succeed
  const stateProfileId = (location.state as { profileId?: string } | null)?.profileId;
  const stateToken = (location.state as { token?: string } | null)?.token;

  const [step, setStep] = useState<Step>(() => {
    if (consentPending) return 'waiting';
    if (stateInitialStep) return stateInitialStep;
    return 'dob';
  });

  // DOB step state
  const [month, setMonth] = useState<number | ''>('');
  const [year, setYear] = useState<number | ''>('');
  const [dobError, setDobError] = useState('');

  // child-info step state — pre-filled when redirected from the OAuth name step
  const [explorerName, setExplorerName] = useState(stateExplorerName);
  const [consentEmail, setConsentEmail] = useState('');
  const [childInfoError, setChildInfoError] = useState('');
  const [childInfoLoading, setChildInfoLoading] = useState(false);

  // waiting step state — profileId established after createProfile succeeds.
  // stateProfileId + stateToken are set when navigated from AuthPage's inline under-13 form.
  const [createdProfileId, setCreatedProfileId] = useState<string | null>(() =>
    consentPending ? (stateProfileId ?? profile?.id ?? null) : null
  );
  const parentEmail = profile?.parentEmail ?? consentEmail;
  const [consentToken, setConsentToken] = useState<string | null>(() => {
    if (!consentPending) return null;
    return stateToken ?? sessionStorage.getItem('omore_consent_token') ?? null;
  });
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'exhausted'>('idle');

  // Edit parent email state
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [newParentEmail, setNewParentEmail] = useState('');
  const [editEmailStatus, setEditEmailStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [editEmailError, setEditEmailError] = useState('');

  // password-setup step state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Stored DOB for passing to createProfile in child-info.
  // Seeded from route state when redirected here from AuthPage DOB drum.
  const [storedDobIso, setStoredDobIso] = useState(() => stateDob ?? '');

  // If the user arrives already authenticated with a DOB but no profile (13+ returning
  // to auth after the DOB step), skip straight to /auth for the name step.
  useEffect(() => {
    if (consentPending || step !== 'dob' || !user || profile) return;
    if (sessionStorage.getItem('omore_dob')) {
      navigate('/auth', { replace: true });
    }
  }, [consentPending, step, user, profile, navigate]);

  // If an authenticated user with a pending_consent profile arrives at /age-gate
  // without the consentPending route state (e.g. direct URL navigation), restore
  // the waiting step so they don't see the DOB form again.
  useEffect(() => {
    if (consentPending || !profile || profile.subscriptionStatus !== 'pending_consent') return;
    if (createdProfileId === null) {
      setCreatedProfileId(profile.id);
    }
    if (step === 'dob' || step === 'child-info') {
      setStep('waiting');
    }
  }, [consentPending, profile, createdProfileId, step]);

  // Pre-fill consent email from OAuth credential when the child-info step becomes active
  useEffect(() => {
    if (step !== 'child-info') return;
    const email = auth.currentUser?.email ?? '';
    if (email && !consentEmail) {
      setConsentEmail(email);
    }
  }, [step, consentEmail]);

  // Real-time listener: when parent approves, subscriptionStatus flips to 'active'
  useEffect(() => {
    if (step !== 'waiting' || !createdProfileId || !user) return;

    const profileRef = doc(db, 'appleUsers', user.uid, 'profiles', createdProfileId);
    const unsubscribe = onSnapshot(profileRef, (snap) => {
      if (!snap.exists()) return;
      const status = snap.data()?.subscriptionStatus;
      if (status === 'active') {
        sessionStorage.removeItem('omore_consent_token');
        setProfile({ id: snap.id, ...snap.data() } as Profile);
        setStep('password-setup');
      } else if (status === 'rejected') {
        sessionStorage.removeItem('omore_consent_token');
        setStep('rejected');
      }
    });

    return unsubscribe;
  }, [step, createdProfileId, user, setProfile]);

  function handleDobSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (year === '') {
      setDobError('Please select your birth year.');
      return;
    }
    const skipMonth = (currentYear - (year as number)) >= 19;
    if (!skipMonth && month === '') {
      setDobError('Please select your birth month.');
      return;
    }
    setDobError('');
    const effectiveMonth = skipMonth ? 0 : (month as number);
    const age = calculateAge(effectiveMonth, year as number);

    if (age < 5 || age > 30) {
      setDobError('Please check your date of birth and try again.');
      return;
    }

    const dobIso = new Date(year as number, effectiveMonth, 1).toISOString();

    if (age < 13) {
      // Under-13: stay in the age gate; advance to child-info inline
      setStoredDobIso(dobIso);
      setStep('child-info');
    } else {
      // 13+: hand off to /auth for the standard sign-in/name step
      sessionStorage.setItem('omore_dob', dobIso);
      navigate('/auth', { replace: true });
    }
  }

  async function handleChildInfoSubmit(e: React.FormEvent) {
    e.preventDefault();
    setChildInfoError('');

    const name = explorerName.trim();
    const email = consentEmail.trim();

    if (name.length < 2 || name.length > 30) {
      setChildInfoError('Explorer name must be between 2 and 30 characters.');
      return;
    }
    if (!/^[a-zA-Z0-9 ]+$/.test(name)) {
      setChildInfoError('Explorer name can only contain letters, numbers, and spaces.');
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setChildInfoError('Please enter a valid email address for the consent notification.');
      return;
    }

    setChildInfoLoading(true);
    try {
      // Ensure we have a Firebase Auth session. Anonymous auth if the user
      // hasn't signed in via Apple/Google yet.
      let uid = auth.currentUser?.uid;
      if (!uid) {
        const cred = await signInAnonymously();
        uid = cred.user.uid;
      }

      const { data: profileData } = await createProfile({
        uid,
        explorerName: name,
        dateOfBirth: storedDobIso,
        signupPlatform: 'web',
        subscriptionStatus: 'pending_consent',
        parentEmail: email,
      });

      const { data: consentData } = await initiateParentConsent({
        childDisplayName: name,
        parentEmail: email,
        profileId: profileData.profileId,
        dateOfBirth: storedDobIso,
      });

      sessionStorage.setItem('omore_consent_token', consentData.token);
      setCreatedProfileId(profileData.profileId);
      setConsentToken(consentData.token);
      setStep('waiting');
    } catch (err: unknown) {
      const fbErr = err as { code?: string; message?: string };
      if (fbErr.code === 'functions/already-exists') {
        setChildInfoError('That explorer name is already taken. Please choose another.');
      } else {
        setChildInfoError(fbErr.message ?? 'Something went wrong. Please try again.');
      }
    } finally {
      setChildInfoLoading(false);
    }
  }

  async function handleResend() {
    if (!consentToken || resendStatus === 'sending' || resendStatus === 'exhausted') return;
    setResendStatus('sending');
    try {
      const result = await resendConsentEmail({ token: consentToken });
      // CF returns a newToken when the original was expired and a fresh one was minted.
      const freshToken = (result.data as { newToken?: string }).newToken;
      if (freshToken) {
        setConsentToken(freshToken);
        sessionStorage.setItem('omore_consent_token', freshToken);
      }
      setResendStatus('sent');
      setTimeout(() => setResendStatus('idle'), 3000);
    } catch (err: unknown) {
      const fbErr = err as { code?: string };
      if (fbErr.code === 'functions/resource-exhausted') {
        setResendStatus('exhausted');
      } else {
        setResendStatus('idle');
      }
    }
  }

  async function handleEditEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!createdProfileId || !user) return;
    setEditEmailError('');
    setEditEmailStatus('saving');
    try {
      const result = await updateConsentEmail({
        profileId: createdProfileId,
        newParentEmail: newParentEmail.trim(),
      });
      // Swap to the fresh token so resend keeps working
      setConsentToken(result.data.token);
      sessionStorage.setItem('omore_consent_token', result.data.token);
      setEditEmailStatus('saved');
      setShowEditEmail(false);
      setNewParentEmail('');
    } catch (err: unknown) {
      const fbErr = err as { message?: string };
      setEditEmailError(fbErr.message ?? 'Could not update email. Please try again.');
      setEditEmailStatus('error');
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError('');

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    if (!createdProfileId || !user) {
      setPasswordError('Session error — please refresh and try again.');
      return;
    }

    setPasswordLoading(true);
    try {
      await finalizeUnder13Credentials({ profileId: createdProfileId, password });
      await loadAndNavigate();
    } catch (err: unknown) {
      const fbErr = err as { message?: string };
      setPasswordError(fbErr.message ?? 'Something went wrong. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  }

  async function handlePasswordSkip() {
    await loadAndNavigate();
  }

  async function loadAndNavigate() {
    if (!user) return;
    try {
      const profiles = await loadProfiles(user.uid);
      if (profiles.length === 1) setProfile(profiles[0]);
    } catch {
      // Profile was already set by the onSnapshot listener; safe to continue
    }
    navigate('/onboarding', { replace: true });
  }

  return (
    <AnimatedPage>
      <div
        className={`flex min-h-screen items-center justify-center px-4 py-12 transition-colors duration-500 ${
          step === 'waiting' ? 'bg-base' : ''
        }`}
      >
        <motion.div
          layout
          className={`w-full max-w-md rounded-2xl ${
            step === 'waiting' ? '' : 'bg-white p-8 shadow-lg'
          }`}
        >
          <AnimatePresence mode="wait">

            {/* ── Step 1: DOB ── */}
            {step === 'dob' && (
              <motion.div
                key="dob"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h1 className="text-2xl font-bold text-gray-900">How Old Are You?</h1>
                <p className="mt-2 text-sm text-gray-500">
                  We ask so we can create the right experience for you.
                </p>

                <form onSubmit={handleDobSubmit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="year" className="block text-sm font-medium text-gray-700">
                      Birth Year
                    </label>
                    <select
                      id="year"
                      value={year}
                      onChange={(e) => {
                        setYear(e.target.value === '' ? '' : Number(e.target.value));
                        setMonth('');
                      }}
                      className="mt-1 block w-full rounded-lg border-gray-300 text-gray-900 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      required
                    >
                      <option value="">Year</option>
                      {YEARS.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>

                  {year !== '' && (currentYear - (year as number)) < 19 && (
                    <div>
                      <label htmlFor="month" className="block text-sm font-medium text-gray-700">
                        Birth Month
                      </label>
                      <select
                        id="month"
                        value={month}
                        onChange={(e) =>
                          setMonth(e.target.value === '' ? '' : Number(e.target.value))
                        }
                        className="mt-1 block w-full rounded-lg border-gray-300 text-gray-900 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                        required
                      >
                        <option value="">Month</option>
                        {MONTHS.map((m, i) => (
                          <option key={m} value={i}>{m}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {dobError && (
                    <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
                      {dobError}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="flex min-h-touch w-full items-center justify-center rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
                  >
                    Continue
                  </button>
                </form>
              </motion.div>
            )}

            {/* ── Step 2: Explorer name + consent email (under-13 only) ── */}
            {step === 'child-info' && (
              <motion.div
                key="child-info"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h1 className="text-2xl font-bold text-gray-900">Tell Us About You</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Pick your explorer name, then enter an email where we can send a quick
                  approval — yours or a parent's.
                </p>

                <form onSubmit={handleChildInfoSubmit} className="mt-6 space-y-4">
                  <div>
                    <label
                      htmlFor="explorerName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Your Explorer Name
                    </label>
                    <input
                      id="explorerName"
                      type="text"
                      value={explorerName}
                      onChange={(e) => setExplorerName(e.target.value)}
                      maxLength={30}
                      placeholder="e.g. StarBuilder"
                      className="mt-1 block w-full rounded-lg border-gray-300 text-gray-900 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="consentEmail"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Parent or Guardian's Email
                    </label>
                    <p className="mt-0.5 text-xs text-gray-400">
                      We'll send a consent approval to this address. Must be a parent or guardian's email.
                    </p>
                    <input
                      id="consentEmail"
                      type="email"
                      value={consentEmail}
                      onChange={(e) => setConsentEmail(e.target.value)}
                      placeholder="parent@example.com"
                      className="mt-1 block w-full rounded-lg border-gray-300 text-gray-900 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      required
                    />
                  </div>

                  {childInfoError && (
                    <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
                      {childInfoError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={childInfoLoading}
                    className="flex min-h-touch w-full items-center justify-center rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
                  >
                    {childInfoLoading ? 'Sending approval email…' : 'Send Approval Email'}
                  </button>
                </form>
              </motion.div>
            )}

            {/* ── Step 3: Waiting for consent ── */}
            {step === 'waiting' && (
              <motion.div
                key="waiting"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="rounded-2xl bg-base px-6 py-10 text-center text-ink"
              >
                <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center">
                  <span className="absolute inset-0 animate-[duo-pulse_2s_ease-in-out_infinite] rounded-full bg-amber/30" />
                  <span className="absolute inset-3 animate-[duo-pulse_2s_ease-in-out_infinite_0.4s] rounded-full bg-amber/20" />
                  <span className="relative text-5xl">🔒</span>
                </div>

                <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink">
                  Adventure Incoming!
                </h1>
                <p className="mt-3 text-sm text-ink/70">
                  One quick thing — we need an approval first.
                </p>
                <p className="mt-2 text-sm text-ink/70">
                  We emailed{' '}
                  <span className="font-semibold text-amber">{parentEmail}</span>.
                  Once they tap <span className="font-semibold text-mint">Approve</span>,
                  your account unlocks automatically! 🚀
                </p>

                <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber/30 bg-amber/10 px-4 py-2 text-xs font-semibold text-amber">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-amber" />
                  Waiting for approval…
                </div>

                <p className="mt-4 text-xs text-ink/40">
                  Ask them to check their inbox (and spam folder just in case).
                </p>

                <a
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block text-xs text-amber/70 underline underline-offset-2 hover:text-amber"
                >
                  Parents: review &amp; manage consent at parent.omore.app →
                </a>

                <div className="mt-6 space-y-2">
                  {consentToken ? (
                    <button
                      onClick={handleResend}
                      disabled={resendStatus === 'sending' || resendStatus === 'exhausted'}
                      className="duo-btn duo-btn-ghost w-full text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {resendStatus === 'sending' && '✉️ Sending…'}
                      {resendStatus === 'sent' && '✅ Email resent!'}
                      {resendStatus === 'exhausted' && 'Resend limit reached'}
                      {resendStatus === 'idle' && '✉️ Resend email'}
                    </button>
                  ) : (
                    <p className="text-xs text-ink/40">
                      Didn&apos;t get the email? Ask your grown-up to check spam.
                    </p>
                  )}

                  {/* Edit parent email */}
                  {!showEditEmail ? (
                    <button
                      onClick={() => { setShowEditEmail(true); setEditEmailError(''); setEditEmailStatus('idle'); }}
                      className="w-full text-sm text-ink/40 underline-offset-2 hover:text-ink/60 hover:underline"
                    >
                      ✏️ Wrong email? Change it
                    </button>
                  ) : (
                    <form onSubmit={handleEditEmailSubmit} className="mt-2 space-y-2 text-left">
                      <input
                        type="email"
                        required
                        value={newParentEmail}
                        onChange={(e) => { setNewParentEmail(e.target.value); setEditEmailError(''); }}
                        placeholder="new-parent@example.com"
                        className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-ink placeholder-ink/30 focus:outline-none focus:ring-2 focus:ring-amber/50"
                      />
                      {editEmailError && (
                        <p className="text-xs text-red-400">{editEmailError}</p>
                      )}
                      {editEmailStatus === 'saved' && (
                        <p className="text-xs text-mint">✅ New approval email sent!</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={editEmailStatus === 'saving'}
                          className="duo-btn duo-btn-orange flex-1 text-sm disabled:opacity-50"
                        >
                          {editEmailStatus === 'saving' ? 'Sending…' : 'Send to new email'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowEditEmail(false); setNewParentEmail(''); setEditEmailError(''); }}
                          className="rounded-xl border border-white/20 px-4 text-sm text-ink/50 hover:text-ink/70"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}

                  <button
                    onClick={() => navigate('/', { replace: true })}
                    className="w-full text-sm text-ink/40 underline-offset-2 hover:text-ink/60 hover:underline"
                  >
                    Back to home
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── Step 4: Post-consent password setup ── */}
            {step === 'password-setup' && (
              <motion.div
                key="password-setup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h1 className="text-2xl font-bold text-gray-900">You're In! 🎉</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Create a password so you can sign in with your explorer name next time.
                  {isOAuthUser() && ' You can also skip this and keep signing in with Apple or Google.'}
                </p>

                <form onSubmit={handlePasswordSubmit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={8}
                      placeholder="At least 8 characters"
                      className="mt-1 block w-full rounded-lg border-gray-300 text-gray-900 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Confirm Password
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat your password"
                      className="mt-1 block w-full rounded-lg border-gray-300 text-gray-900 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      required
                    />
                  </div>

                  {passwordError && (
                    <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
                      {passwordError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex min-h-touch w-full items-center justify-center rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
                  >
                    {passwordLoading ? 'Saving…' : 'Set Password & Continue'}
                  </button>

                  {isOAuthUser() && (
                    <button
                      type="button"
                      onClick={handlePasswordSkip}
                      className="w-full text-sm text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                    >
                      Skip for now — I'll use Apple/Google to sign in
                    </button>
                  )}
                </form>
              </motion.div>
            )}

            {/* ── Step: Rejected — parent declined, try again ── */}
            {step === 'rejected' && (
              <motion.div
                key="rejected"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
              >
                <div className="mb-4 text-center text-5xl">😔</div>
                <h1 className="text-center text-2xl font-bold text-gray-900">
                  Your grown-up didn't approve this time
                </h1>
                <p className="mt-2 text-center text-sm text-gray-500">
                  That's okay — you can try again with a different email address.
                </p>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!createdProfileId || !user) return;
                    setEditEmailError('');
                    setEditEmailStatus('saving');
                    try {
                      const result = await updateConsentEmail({
                        profileId: createdProfileId,
                        newParentEmail: newParentEmail.trim(),
                      });
                      setConsentToken(result.data.token);
                      sessionStorage.setItem('omore_consent_token', result.data.token);
                      setEditEmailStatus('idle');
                      setNewParentEmail('');
                      setStep('waiting');
                    } catch (err: unknown) {
                      const fbErr = err as { message?: string };
                      setEditEmailError(fbErr.message ?? 'Something went wrong. Please try again.');
                      setEditEmailStatus('error');
                    }
                  }}
                  className="mt-6 space-y-4"
                >
                  <div>
                    <label htmlFor="retry-email" className="block text-sm font-medium text-gray-700">
                      Parent / Guardian's Email
                    </label>
                    <input
                      id="retry-email"
                      type="email"
                      required
                      value={newParentEmail}
                      onChange={(e) => { setNewParentEmail(e.target.value); setEditEmailError(''); }}
                      placeholder="another-parent@example.com"
                      className="mt-1 block w-full rounded-lg border-gray-300 text-gray-900 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                    />
                  </div>

                  {editEmailError && (
                    <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
                      {editEmailError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={editEmailStatus === 'saving'}
                    className="flex min-h-touch w-full items-center justify-center rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
                  >
                    {editEmailStatus === 'saving' ? 'Sending…' : 'Send New Approval Request'}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate('/', { replace: true })}
                    className="w-full text-sm text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                  >
                    Back to home
                  </button>
                </form>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>
      </div>
    </AnimatedPage>
  );
}
